/**
 * SurveyStationsPanel — the station DataTable + KPI band + bulk paste-import +
 * manual add-row (§5, §26). Server-computed trajectory columns (TVD / N-S /
 * E-W / DLS) render read-only; the FE never computes them (min-curvature lives
 * on the OXBO-x compute-server). New stations (pasted or typed) are appended
 * optimistically with their computed columns left pending ("auto") until the
 * write + server compute round-trips — the paste block is `MD Inc Azi`.
 *
 * Data is served by Oxbo-P (GET /v1/wells/{uwi}/surveys) and owned by P
 * ("whoever pays owns it") — this screen reads it and shapes it, mirroring the
 * cost / NPT panels.
 */
import { useMemo, useRef, useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { useUnits } from "@units/useUnits";
import { fmtNum } from "@lib/format";
import { StatBand, type Stat } from "@components/ui/StatBand";
import { PasteImport, InlineRowGrid, type InlineColumn } from "@components/input";
import { CatalogPicker } from "@components/catalog";
import type { SurveyStationOut } from "@api/types";
import { SurveyTrajectory } from "./SurveyTrajectory";
import {
  DLS_SOFT_WARN,
  sortByMd,
  surveyKpis,
  trajectoryPoints,
  validateStation,
} from "./surveyMetrics";

interface Props {
  stations: SurveyStationOut[];
  wellUwi: string;
  eventId: string;
  /** The event's stored unit profile (measurements are stored in it). */
  unitProfileKey: string | null | undefined;
  /** Report closed → lock entry (no add / paste). */
  readOnly: boolean;
}

/** Entry-row draft for a manually-added station. */
interface StationDraft {
  md: string;
  inc: string;
  azi: string;
  tool: string | null;
}

const emptyDraft = (): StationDraft => ({ md: "", inc: "", azi: "", tool: "mwd" });

/** A locally-added station (optimistic; computed columns pending). */
function pendingStation(
  md: number,
  inc: number,
  azi: number,
  tool: string | null,
  eventId: string,
  wellUwi: string,
  seq: number,
): SurveyStationOut {
  return {
    id: `local-${eventId}-${seq}`,
    event_id: eventId,
    well_uwi: wellUwi,
    md,
    inc,
    azi,
    tvd: null,
    ns: null,
    ew: null,
    dls: null,
    survey_tool_entry_key: tool,
    date_survey: new Date().toISOString().slice(0, 10),
    carry_over: false,
  };
}

export function SurveyStationsPanel({
  stations,
  wellUwi,
  eventId,
  unitProfileKey,
  readOnly,
}: Props) {
  const { t, locale } = useLocale();
  const { convertField, fmt } = useUnits(unitProfileKey);

  // Locally-added stations (paste + manual). In a live build these POST to
  // /v1/events/{id}/surveys (single) or bulk-upsert; the endpoint is not live in
  // standalone, so they append optimistically with computed columns pending.
  const [added, setAdded] = useState<SurveyStationOut[]>([]);
  const seqRef = useRef(0);

  const allStations = useMemo(
    () => sortByMd([...stations, ...added]),
    [stations, added],
  );

  const kpis = useMemo(() => surveyKpis(allStations), [allStations]);
  const points = useMemo(() => trajectoryPoints(allStations), [allStations]);

  // Dynamic length unit for the axis labels + column headers (ft / m).
  const lengthUnit = convertField("survey_station", "md", 0, unitProfileKey).unit;
  const lu = lengthUnit ? ` (${lengthUnit})` : "";

  /** Convert + format one stored length value into the display profile. */
  const len = (v: number | null): string =>
    v == null ? t("common.na") : fmt(convertField("survey_station", "md", v, unitProfileKey));

  const stats: Stat[] = [
    { label: t("surveys.kpi.currentMd"), value: len(kpis.currentMd), tone: "ops" },
    { label: t("surveys.kpi.currentTvd"), value: len(kpis.currentTvd), tone: "ops" },
    {
      label: t("surveys.kpi.maxInc"),
      value: kpis.maxInc == null ? t("common.na") : `${fmtNum(kpis.maxInc, locale, { maximumFractionDigits: 1 })}°`,
    },
    {
      label: t("surveys.kpi.maxDls"),
      value:
        kpis.maxDls == null
          ? t("common.na")
          : `${fmtNum(kpis.maxDls, locale, { maximumFractionDigits: 2 })}${t("surveys.unit.dls")}`,
      tone: kpis.dlsWarn ? "warn" : undefined,
    },
    {
      label: t("surveys.kpi.closure"),
      value:
        kpis.closure == null
          ? t("common.na")
          : `${len(kpis.closure)} @ ${fmtNum(kpis.closureAzimuth, locale, { maximumFractionDigits: 1 })}°`,
    },
  ];

  const addStations = (candidates: { md: number; inc: number; azi: number; tool: string | null }[]) => {
    setAdded((prev) => {
      const known = sortByMd([...stations, ...prev]);
      const next = [...prev];
      for (const c of candidates) {
        // Enforce MD strictly-increasing against everything already known.
        if (validateStation(c, [...known, ...next.slice(prev.length)]).some((i) => i.kind === "mdOrder")) {
          continue;
        }
        next.push(pendingStation(c.md, c.inc, c.azi, c.tool, eventId, wellUwi, seqRef.current++));
      }
      return next;
    });
  };

  const onPaste = (rows: { values: string[] }[]) => {
    const parsed = rows
      .map((r) => ({
        md: Number(r.values[0]),
        inc: Number(r.values[1]),
        azi: Number(r.values[2]),
        tool: "mwd" as string | null,
      }))
      .filter(
        (c) =>
          Number.isFinite(c.md) &&
          Number.isFinite(c.inc) &&
          Number.isFinite(c.azi) &&
          validateStation(c, []).filter((i) => i.kind !== "mdOrder").length === 0,
      );
    addStations(parsed);
  };

  const numHeader = (label: string) => <span>{label}</span>;
  const computedHeader = (label: string) => (
    <span>
      {label}
      <span className="calc-hint">{t("surveys.col.calc")}</span>
    </span>
  );

  const columns: InlineColumn<SurveyStationOut, StationDraft>[] = [
    {
      key: "md",
      header: numHeader(`${t("surveys.col.md")}${lu}`),
      num: true,
      render: (r) => len(r.md),
      input: (d, patch) => (
        <input
          type="number"
          inputMode="decimal"
          placeholder={t("surveys.col.md")}
          aria-label={t("surveys.col.md")}
          value={d.md}
          onChange={(e) => patch({ md: e.target.value })}
        />
      ),
    },
    {
      key: "inc",
      header: numHeader(t("surveys.col.inc")),
      num: true,
      render: (r) => fmtNum(r.inc, locale, { maximumFractionDigits: 1 }),
      input: (d, patch) => (
        <input
          type="number"
          inputMode="decimal"
          min={0}
          max={120}
          placeholder={t("surveys.col.inc")}
          aria-label={t("surveys.col.inc")}
          value={d.inc}
          onChange={(e) => patch({ inc: e.target.value })}
        />
      ),
    },
    {
      key: "azi",
      header: numHeader(t("surveys.col.azi")),
      num: true,
      render: (r) => fmtNum(r.azi, locale, { maximumFractionDigits: 1 }),
      input: (d, patch) => (
        <input
          type="number"
          inputMode="decimal"
          min={0}
          max={360}
          placeholder={t("surveys.col.azi")}
          aria-label={t("surveys.col.azi")}
          value={d.azi}
          onChange={(e) => patch({ azi: e.target.value })}
        />
      ),
    },
    {
      key: "tvd",
      header: computedHeader(`${t("surveys.col.tvd")}${lu}`),
      num: true,
      computed: true,
      render: (r) => (r.tvd == null ? <span className="dim">{t("surveys.col.pending")}</span> : len(r.tvd)),
    },
    {
      key: "ns",
      header: computedHeader(`${t("surveys.col.ns")}${lu}`),
      num: true,
      computed: true,
      render: (r) => (r.ns == null ? <span className="dim">{t("surveys.col.pending")}</span> : len(r.ns)),
    },
    {
      key: "ew",
      header: computedHeader(`${t("surveys.col.ew")}${lu}`),
      num: true,
      computed: true,
      render: (r) => (r.ew == null ? <span className="dim">{t("surveys.col.pending")}</span> : len(r.ew)),
    },
    {
      key: "dls",
      header: computedHeader(`${t("surveys.col.dls")}`),
      num: true,
      computed: true,
      render: (r) =>
        r.dls == null ? (
          <span className="dim">{t("surveys.col.pending")}</span>
        ) : (
          <span className={r.dls >= DLS_SOFT_WARN ? "var-bad" : undefined}>
            {fmtNum(r.dls, locale, { maximumFractionDigits: 2 })}
            {r.dls >= DLS_SOFT_WARN ? (
              <span className="badge" style={{ marginLeft: 6 }} title={t("surveys.dls.warnTitle")}>
                {t("surveys.dls.warn")}
              </span>
            ) : null}
          </span>
        ),
    },
    {
      key: "tool",
      header: numHeader(t("surveys.col.tool")),
      render: (r) => r.survey_tool_entry_key ?? t("common.na"),
      input: (d, patch) => (
        <CatalogPicker
          catalogKey="survey_tools"
          value={d.tool}
          onChange={(code) => patch({ tool: code })}
          placeholder={t("surveys.col.tool")}
          compact
          ariaLabel={t("surveys.col.tool")}
        />
      ),
    },
    {
      key: "date",
      header: numHeader(t("surveys.col.date")),
      render: (r) => <span className="tab-nums">{r.date_survey ?? t("common.na")}</span>,
    },
  ];

  const toRow = (d: StationDraft): SurveyStationOut | null => {
    const md = Number(d.md);
    const inc = Number(d.inc);
    const azi = Number(d.azi);
    if (!(d.md.trim() && d.inc.trim() && d.azi.trim())) return null;
    if (!Number.isFinite(md) || !Number.isFinite(inc) || !Number.isFinite(azi)) return null;
    const issues = validateStation({ md, inc, azi }, [...stations, ...added]);
    if (issues.length) return null;
    return pendingStation(md, inc, azi, d.tool, eventId, wellUwi, seqRef.current++);
  };

  return (
    <div className="surveys-panel">
      <StatBand stats={stats} />

      {!readOnly ? (
        <div className="card" style={{ marginTop: 14 }}>
          <PasteImport
            cols={3}
            onParse={onPaste}
            title={t("surveys.paste.title")}
            hint={t("surveys.paste.hint")}
            computedNote={t("surveys.paste.note")}
            placeholder={t("surveys.paste.placeholder")}
            actionLabel={t("surveys.paste.action")}
          />
        </div>
      ) : null}

      <div className="card" style={{ marginTop: 14 }}>
        <h3>{t("surveys.stations.title")}</h3>
        <p className="dim" style={{ fontSize: 12.5, marginTop: -4, marginBottom: 12 }}>
          {t("surveys.stations.hint")}
        </p>
        <InlineRowGrid<SurveyStationOut, StationDraft>
          columns={columns}
          rows={allStations}
          rowKey={(r) => r.id}
          emptyDraft={emptyDraft}
          toRow={toRow}
          onCommit={(row) => setAdded((prev) => [...prev, row])}
          isCarried={(r) => r.carry_over}
          rowOwner={(r) => (r.id.startsWith("local-") ? "z" : "p")}
          readOnly={readOnly}
          ariaLabel={t("surveys.stations.title")}
          emptyLabel={t("surveys.stations.empty")}
        />
        <p className="note" style={{ fontSize: 12.5, marginTop: 12 }}>
          {t("surveys.compute.note")}
        </p>
      </div>

      <SurveyTrajectory points={points} lengthUnit={lengthUnit} />
    </div>
  );
}
